/**
 * useOrbSocket — Socket.IO hook for the Orb Voice Gateway
 * =========================================================
 * Connects to backend Socket.IO, authenticates via JWT,
 * and handles the full Orb protocol:
 *   orb:init → orb:audio → orb:state / orb:response
 *
 * Returns: { orbState, transcript, responseText, isConnected, sendAudio, sendDone }
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

// ── Socket.IO connection config ──────────────────────────
// Priority: REACT_APP_SOCKET_URL > REACT_APP_BACKEND_URL > localhost:8000
// Path:     REACT_APP_SOCKET_PATH > auto-detect
//
// On Emergent/k8s: path = /api/socket.io (ingress routes /api/* to backend)
// On production (Cloudflare → uvicorn): path = /socket.io (direct)
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL
  || process.env.REACT_APP_BACKEND_URL
  || 'http://127.0.0.1:8000';

// On Emergent/k8s AND user production: path = /api/socket.io
// The backend always mounts at /api/socket.io via SOCKETIO_PATH env
// k8s ingress routes /api/* to backend, Cloudflare must do the same
const SOCKET_PATH = process.env.REACT_APP_SOCKET_PATH || '/api/socket.io';

export default function useOrbSocket(enabled = true) {
  const [orbState, setOrbState] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [responseText, setResponseText] = useState('');
  const [responseAudio, setResponseAudio] = useState(null); // { b64, mimeType }
  const [audioFailed, setAudioFailed] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isGuardian, setIsGuardian] = useState(false);
  const [error, setError] = useState(null);
  const [matchResult, setMatchResult] = useState(null);

  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const audioPlayingRef = useRef(false);
  const greetingReceivedRef = useRef(false);

  // Connect + authenticate — re-runs when `enabled` changes (after login)
  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setIsConnected(false);
      return;
    }

    const lang = localStorage.getItem('i18nextLng') || 'de';

    console.log(`[OrbSocket] Connecting to ${SOCKET_URL} path=${SOCKET_PATH}`);

    const socket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      withCredentials: false,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      // Reset all stale state from previous session
      audioPlayingRef.current = false;
      setResponseAudio(null);
      setResponseText('');
      setAudioFailed(false);
      setOrbState('idle');
      // Authenticate
      socket.emit('orb:init', { token, lang });
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      setOrbState('idle');
    });

    socket.on('connect_error', (err) => {
      setError(`Connection failed: ${err.message}`);
      setIsConnected(false);
    });

    // ── Orb protocol events ──

    socket.on('orb:state', (data) => {
      const newState = data?.state?.toLowerCase() || 'idle';
      // Block state updates while audio is playing (prevents infinite loop)
      if (audioPlayingRef.current && (newState === 'idle' || newState === 'listening')) {
        console.log(`[OrbSocket] Blocked state → '${newState}' (audio still playing)`);
        return;
      }
      setOrbState(newState);
      if (newState === 'idle') {
        setIsGuardian(false);
      }
    });

    socket.on('orb:response', (data) => {
      // Skip duplicate greetings on reconnect
      if (data?.is_greeting && greetingReceivedRef.current) {
        console.log('[OrbSocket] Skipping duplicate greeting on reconnect');
        return;
      }
      if (data?.is_greeting) {
        greetingReceivedRef.current = true;
      }

      setTranscript(data?.transcript || '');
      setResponseText(data?.text || '');
      setIsGuardian(!!data?.guardian);
      // Mark audio as playing to block state resets
      audioPlayingRef.current = true;

      if (data?.audio_failed || (!data?.audio && !data?.guardian)) {
        // No audio — frontend must handle (Web Speech or skip)
        setResponseAudio(null);
        setAudioFailed(true);
      } else if (data?.audio && !data?.guardian) {
        setAudioFailed(false);
        setResponseAudio({
          b64: data.audio,
          mimeType: data.mime_type || 'audio/mpeg',
        });
      } else {
        setResponseAudio(null);
        setAudioFailed(false);
      }
    });

    socket.on('orb:error', (data) => {
      setError(data?.message || 'Unknown error');
      setOrbState('idle');
    });

    // Live match push — backend emits when a ride match is found
    socket.on('orb:match', (data) => {
      if (data && typeof data === 'object') {
        setMatchResult(data);
      }
    });

    socket.on('orb:match:clear', () => {
      setMatchResult(null);
    });

    socket.on('orb:pong', () => {
      // Heartbeat acknowledged — connection alive
    });

    // Heartbeat every 25s
    const heartbeat = setInterval(() => {
      if (socket.connected) {
        socket.emit('orb:ping');
      }
    }, 25000);

    return () => {
      clearInterval(heartbeat);
      clearTimeout(reconnectTimer.current);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  // Send audio — via Socket.IO if connected, else REST fallback
  const sendAudio = useCallback((base64Audio, mimeType = 'audio/webm') => {
    const socket = socketRef.current;
    if (socket?.connected) {
      setOrbState('processing');
      socket.emit('orb:audio', { audio: base64Audio, mime_type: mimeType });
      return;
    }
    // REST fallback: POST to /api/voice/orb/pipeline
    setOrbState('processing');
    const token = localStorage.getItem('token');
    fetch(`${SOCKET_URL}/api/voice/orb/pipeline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ audio: base64Audio, mime_type: mimeType }),
    })
      .then(r => r.json())
      .then(data => {
        setTranscript(data?.transcript || '');
        setResponseText(data?.text || '');
        setIsGuardian(!!data?.guardian);
        if (data?.tts_failed || (!data?.audio_b64 && !data?.guardian)) {
          setResponseAudio(null);
          setAudioFailed(true);
        } else if (data?.audio_b64 && !data?.guardian) {
          setAudioFailed(false);
          setResponseAudio({ b64: data.audio_b64, mimeType: data.mime_type || 'audio/mpeg' });
        } else {
          setResponseAudio(null);
          setAudioFailed(false);
        }
        setOrbState(data?.guardian ? 'guardian' : 'speaking');
      })
      .catch(err => {
        setError(`Fehler: ${err.message}`);
        setOrbState('idle');
      });
  }, []);

  // Signal audio playback is done → back to idle, user taps to speak
  const sendDone = useCallback(() => {
    audioPlayingRef.current = false;
    // CRITICAL: Clear audio data to prevent replay loop
    // Without this, the OrbPage audio effect re-triggers on orbState change
    // and finds stale responseAudio data → plays again → infinite loop
    setResponseAudio(null);
    setResponseText('');
    setAudioFailed(false);
    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit('orb:done');
    }
    setOrbState('idle');
  }, []);

  return {
    orbState,
    transcript,
    responseText,
    responseAudio,
    audioFailed,
    isConnected,
    isGuardian,
    matchResult,
    clearMatch: () => setMatchResult(null),
    error,
    sendAudio,
    sendDone,
  };
}
